import React, { useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

/**
 * Structured List Component
 * Renders various types of lists with optional icons and actions
 */
const StructuredList = ({
  items = [],
  title,
  type = 'unordered', // 'ordered', 'unordered', 'checklist', 'custom'
  variant = 'default', // 'default', 'compact', 'detailed'
  showIcons = false,
  clickable = false,
  onItemClick,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const handleItemClick = (item, index) => {
    if (clickable && onItemClick) {
      onItemClick(item, index);
    }
  };

  const renderIcon = (item) => {
    if (!showIcons && !item.icon) return null;

    const iconContent = item.icon || getDefaultIcon(item, type);

    return <span style={styles.icon}>{typeof iconContent === 'string' ? iconContent : iconContent}</span>;
  };

  const getDefaultIcon = (item, listType) => {
    switch (listType) {
      case 'checklist':
        return item.checked ? '✅' : '⬜';
      case 'ordered':
        return null; // Numbers handled by CSS
      case 'unordered':
        return '•';
      default:
        return '•';
    }
  };

  const renderItemContent = (item) => {
    if (typeof item === 'string') {
      return <span style={styles.itemText}>{item}</span>;
    }

    return (
      <>
        <div style={styles.itemMain}>
          <span style={styles.itemText}>{item.text || item.title}</span>
          {item.badge && (
            <span
              style={{
                ...styles.badge,
                backgroundColor: item.badge.color || theme.colors.primary,
              }}
            >
              {item.badge.text}
            </span>
          )}
        </div>

        {item.description && variant !== 'compact' && <div style={styles.itemDescription}>{item.description}</div>}

        {item.actions && variant === 'detailed' && (
          <div style={styles.itemActions}>
            {item.actions.map((action, actionIndex) => (
              <button
                key={actionIndex}
                onClick={(e) => {
                  e.stopPropagation();
                  if (action.onClick) action.onClick();
                }}
                style={styles.actionButton}
              >
                {action.icon && <span style={styles.actionIcon}>{action.icon}</span>}
                {action.text}
              </button>
            ))}
          </div>
        )}
      </>
    );
  };

  const getListItemStyles = (item, index) => {
    const baseStyles = {
      ...styles.listItem,
      ...(variant === 'compact' && styles.compactItem),
      ...(clickable && styles.clickableItem),
    };

    // Add status-based styling
    if (typeof item === 'object') {
      if (item.disabled) {
        return { ...baseStyles, ...styles.disabledItem };
      }
      if (item.active) {
        return { ...baseStyles, ...styles.activeItem };
      }
      if (item.status) {
        switch (item.status) {
          case 'success':
            return { ...baseStyles, borderLeftColor: theme.colors.status.success };
          case 'warning':
            return { ...baseStyles, borderLeftColor: theme.colors.status.warning };
          case 'error':
            return { ...baseStyles, borderLeftColor: theme.colors.danger };
          case 'info':
            return { ...baseStyles, borderLeftColor: theme.colors.status.info };
        }
      }
    }

    return baseStyles;
  };

  const styles = {
    container: {
      backgroundColor: theme.colors.background.sidebar,
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.colors.border}`,
      overflow: 'hidden',
      ...style,
    },
    header: {
      padding: theme.spacing.md,
      borderBottom: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background.main,
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text.primary,
      margin: 0,
    },
    list: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    orderedList: {
      counterReset: 'list-counter',
    },
    listItem: {
      display: 'flex',
      alignItems: variant === 'compact' ? 'center' : 'flex-start',
      padding: theme.spacing.md,
      borderBottom: `1px solid ${theme.colors.border}`,
      transition: 'background-color 0.2s',
      borderLeft: '3px solid transparent',
    },
    compactItem: {
      padding: theme.spacing.sm,
    },
    clickableItem: {
      cursor: 'pointer',
    },
    disabledItem: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    activeItem: {
      backgroundColor: theme.colors.background.main,
      borderLeftColor: theme.colors.primary,
    },
    icon: {
      marginRight: theme.spacing.sm,
      fontSize: '16px',
      minWidth: '20px',
      textAlign: 'center',
      color: theme.colors.text.secondary,
    },
    orderNumber: {
      counterIncrement: 'list-counter',
      marginRight: theme.spacing.sm,
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text.secondary,
      minWidth: '20px',
      textAlign: 'center',
    },
    itemContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    itemMain: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: variant === 'detailed' ? theme.spacing.xs : 0,
    },
    itemText: {
      color: theme.colors.text.primary,
      fontSize: '14px',
      lineHeight: '1.5',
      flex: 1,
    },
    itemDescription: {
      color: theme.colors.text.secondary,
      fontSize: '12px',
      lineHeight: '1.4',
      marginTop: theme.spacing.xs,
    },
    badge: {
      fontSize: '10px',
      fontWeight: '600',
      color: theme.colors.text.inverse,
      backgroundColor: theme.colors.primary,
      padding: `2px 6px`,
      borderRadius: theme.borderRadius.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    itemActions: {
      display: 'flex',
      gap: theme.spacing.xs,
      marginTop: theme.spacing.sm,
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.sm,
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      fontSize: '11px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      outline: 'none',
    },
    actionIcon: {
      marginRight: theme.spacing.xs,
      fontSize: '12px',
    },
    emptyState: {
      padding: theme.spacing.xl,
      textAlign: 'center',
      color: theme.colors.text.muted,
      fontStyle: 'italic',
    },
  };

  if (!items || items.length === 0) {
    return (
      <div style={styles.container} className={className}>
        {title && (
          <div style={styles.header}>
            <h3 style={styles.title}>{title}</h3>
          </div>
        )}
        <div style={styles.emptyState}>No items to display</div>
      </div>
    );
  }

  const ListComponent = type === 'ordered' ? 'ol' : 'ul';

  return (
    <div style={styles.container} className={className}>
      {title && (
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
        </div>
      )}

      <ListComponent
        style={{
          ...styles.list,
          ...(type === 'ordered' && styles.orderedList),
        }}
      >
        {items.map((item, index) => (
          <li
            key={index}
            style={getListItemStyles(item, index)}
            onClick={() => handleItemClick(item, index)}
            onMouseEnter={(e) => {
              if (clickable && typeof item === 'object' && !item.disabled) {
                e.currentTarget.style.backgroundColor = theme.colors.background.main;
              }
            }}
            onMouseLeave={(e) => {
              if (clickable) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {type === 'ordered' && <span style={styles.orderNumber}>{index + 1}.</span>}

            {renderIcon(item)}

            <div style={styles.itemContent}>{renderItemContent(item)}</div>
          </li>
        ))}
      </ListComponent>
    </div>
  );
};

export default StructuredList;
